// The page an AI app sends the person to when they add Brandwall as a connector (D-006).
import { useEffect, useState } from "react";
import { ApiError, api, post } from "../api";

interface Info {
  app: { name: string; logoUri: string | null; redirectHost: string };
  brands: { id: string; name: string; role: string; allowed: boolean }[];
  /** The admin MCP: demo walls only, no brands (D-076). */
  admin?: boolean;
}

export function Consent() {
  const query = window.location.search.slice(1);
  const params = Object.fromEntries(new URLSearchParams(query));
  const [info, setInfo] = useState<Info | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Info>(`/v1/oauth/authorize/info?${query}`)
      .then((i) => {
        setInfo(i);
        setPicked(new Set(i.brands.filter((b) => b.allowed).map((b) => b.id)));
      })
      .catch((e) =>
        setError(
          e instanceof ApiError
            ? e.message
            : "This connection link isn't valid. Start again from your AI app.",
        ),
      );
  }, [query]);

  const decide = async (decision: "allow" | "deny") => {
    setBusy(true);
    try {
      const r = await post<{ redirect: string }>("/v1/oauth/authorize", {
        ...params,
        decision,
        workspaces: [...picked],
      });
      window.location.href = r.redirect;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <div className="public">
      <main className="sheet">
        <img src="/logo.svg" alt="Brandwall" style={{ height: 26 }} />
        {error && <div className="error">{error}</div>}
        {info?.admin && (
          <>
            <h1 style={{ fontSize: 22 }}>Connect {info.app.name} to Brandwall admin</h1>
            <p className="muted">
              {info.app.name} can then make and manage demo walls for you: start demos, collect
              posts again, pause them, and get the script that shows a demo wall on a brand's own
              website. It can't reach any brand, customer or setting.
            </p>
            <button
              type="button"
              className="btn block"
              disabled={busy}
              onClick={() => decide("allow")}
            >
              Allow
            </button>
            <button
              type="button"
              className="btn secondary block"
              disabled={busy}
              onClick={() => decide("deny")}
            >
              Cancel
            </button>
            <p className="small muted" style={{ textAlign: "center" }}>
              You'll go back to {info.app.redirectHost}.
            </p>
          </>
        )}
        {info && !info.admin && (
          <>
            <h1 style={{ fontSize: 22 }}>Connect {info.app.name} to Brandwall</h1>
            <p className="muted">
              {info.app.name} can then work in Brandwall for you, for the brands you pick below. On
              each brand's AI & MCP page, you choose what it may do and see everything it did.
            </p>
            <div className="stack" style={{ gap: 8 }}>
              <span className="label">Brands it may use</span>
              {info.brands.map((b) => (
                <label
                  key={b.id}
                  className="row"
                  style={{ fontWeight: 600, opacity: b.allowed ? 1 : 0.5 }}
                >
                  <input
                    type="checkbox"
                    style={{ width: 18, height: 18 }}
                    disabled={!b.allowed}
                    checked={picked.has(b.id)}
                    onChange={(e) => {
                      const n = new Set(picked);
                      e.target.checked ? n.add(b.id) : n.delete(b.id);
                      setPicked(n);
                    }}
                  />
                  <span className="spacer">{b.name}</span>
                  <span className="small muted">{b.allowed ? b.role : "needs Pro"}</span>
                </label>
              ))}
            </div>
            <p className="small muted">
              It can never delete posts or walls, change billing, invite people or change what it's
              allowed to do. You can disconnect it at any time.
            </p>
            <button
              type="button"
              className="btn block"
              disabled={busy || picked.size === 0}
              onClick={() => decide("allow")}
            >
              Allow
            </button>
            <button
              type="button"
              className="btn secondary block"
              disabled={busy}
              onClick={() => decide("deny")}
            >
              Cancel
            </button>
            <p className="small muted" style={{ textAlign: "center" }}>
              You'll go back to {info.app.redirectHost}.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
